import { useAppStore } from '@core/store'
import type { ViewerSurface } from '@viewer/store/views2dSlice'

// ---------------------------------------------------------------------------
// Comutador 3D | 2D da área central do viewer.
//
// Onde vive: JUNTO AO DESENHO, não no ribbon. Três razões:
//   - é o que troca a superfície que está debaixo dele, e uma coisa que
//     troca o que se vê deve estar onde se vê;
//   - o modo mobile não tem ribbon nenhum, e o comutador tem de existir
//     lá também (o mesmo componente serve os dois);
//   - é sempre visível e reversível num clique, esteja-se em que modo
//     se estiver.
// O botão "Vistas 2D" do ribbon continua a existir e entra no modo 2D:
// são duas portas para o mesmo estado do store.
// ---------------------------------------------------------------------------

function SurfaceSwitch({ compact }: { compact?: boolean }) {
  const surface = useAppStore((s) => s.viewerSurface)
  const setSurface = useAppStore((s) => s.setViewerSurface)
  const hasModel = useAppStore((s) => s.models.length > 0)
  // Com o 2D destacado a superfície central é o 3D, mas o comutador não
  // pode ficar com ar de quem está só em 3D: o 2D está vivo, noutra
  // janela. O botão mostra-o (marca ⧉, assinalado) e clicá-lo TRAZ DE
  // VOLTA, porque pôr a superfície em 2D reanexa (ver `views2dSlice`).
  const detached = useAppStore((s) => s.views2dDetached)

  const btn = (value: ViewerSurface, label: string, title: string) => {
    const on = surface === value || (value === '2d' && detached)
    return (
      <button
        type="button"
        onClick={() => setSurface(value)}
        aria-pressed={on}
        data-testid={`surface-switch-${value}`}
        disabled={value === '2d' && !hasModel && surface !== '2d' && !detached}
        title={
          value === '2d' && detached
            ? 'O 2D está numa janela separada. Clica para o trazer de volta para dentro.'
            : value === '2d' && !hasModel
              ? 'Carrega um IFC para gerar plantas e cortes'
              : title
        }
        className={`rounded px-2 ${compact ? 'py-0.5' : 'py-1'} text-[11px] font-semibold transition-colors disabled:opacity-40 ${
          on
            ? value === '2d' && detached
              ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent)] ring-1 ring-inset ring-[var(--color-accent-border)]'
              : 'bg-[var(--color-accent)] text-white'
            : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
        }`}
      >
        {label}
        {value === '2d' && detached ? ' ⧉' : ''}
      </button>
    )
  }

  return (
    <div
      data-testid="surface-switch"
      role="group"
      aria-label="Modo da área central"
      className="flex shrink-0 items-center gap-0.5 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)]/95 p-0.5 shadow-sm backdrop-blur"
    >
      {btn('3d', '3D', 'Modelo tridimensional')}
      {btn('2d', '2D', 'Plantas e cortes: desenho a ocupar a área toda')}
    </div>
  )
}

export default SurfaceSwitch
